import { useEffect, useRef, useState } from "react";

export default function SignaturePad({
  value,
  onChange,
  disabled,
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.max(
      1,
      Math.round(rect.width * dpr)
    );

    canvas.height = Math.max(
      1,
      Math.round(rect.height * dpr)
    );

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    // Evita acumular transformações caso o canvas
    // seja redesenhado mais de uma vez.
    ctx.setTransform(
      dpr,
      0,
      0,
      dpr,
      0,
      0
    );

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#F97316";

    // Se já existir uma assinatura salva,
    // redesenha ela no canvas.
    if (value) {
      const img = new Image();

      img.onload = () => {
        const currentCanvas =
          canvasRef.current;

        if (
          !currentCanvas ||
          currentCanvas !== canvas
        ) {
          return;
        }

        ctx.clearRect(
          0,
          0,
          rect.width,
          rect.height
        );

        ctx.drawImage(
          img,
          0,
          0,
          rect.width,
          rect.height
        );
      };

      img.src = value;
    } else {
      ctx.clearRect(
        0,
        0,
        rect.width,
        rect.height
      );
    }
  }, [value]);

  const getPosition = (event) => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return {
        x: 0,
        y: 0,
      };
    }

    const rect =
      canvas.getBoundingClientRect();

    const touch =
      event.touches?.[0] ||
      event.changedTouches?.[0];

    return {
      x:
        (touch?.clientX ??
          event.clientX) -
        rect.left,

      y:
        (touch?.clientY ??
          event.clientY) -
        rect.top,
    };
  };

  const startDrawing = (event) => {
    if (disabled) {
      return;
    }

    event.preventDefault();

    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx =
      canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    const { x, y } =
      getPosition(event);

    setDrawing(true);

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (event) => {
    if (
      !drawing ||
      disabled
    ) {
      return;
    }

    event.preventDefault();

    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx =
      canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    const { x, y } =
      getPosition(event);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = (
    event
  ) => {
    if (!drawing) {
      return;
    }

    if (event?.preventDefault) {
      event.preventDefault();
    }

    setDrawing(false);

    const canvas =
      canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx =
      canvas.getContext("2d");

    ctx?.closePath();

    onChange?.(
      canvas.toDataURL("image/png")
    );
  };

  const clear = () => {
    if (disabled) {
      return;
    }

    const canvas =
      canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx =
      canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    const rect =
      canvas.getBoundingClientRect();

    ctx.clearRect(
      0,
      0,
      rect.width,
      rect.height
    );

    setDrawing(false);

    onChange?.("");
  };

  return (
    <div>
      <div className="border-2 border-dashed border-border rounded-md bg-background overflow-hidden">
        <canvas
          ref={canvasRef}
          onMouseDown={
            startDrawing
          }
          onMouseMove={draw}
          onMouseUp={
            endDrawing
          }
          onMouseLeave={
            endDrawing
          }
          onTouchStart={
            startDrawing
          }
          onTouchMove={draw}
          onTouchEnd={
            endDrawing
          }
          className={`w-full h-32 touch-none ${
            disabled
              ? "cursor-default opacity-90"
              : "cursor-crosshair"
          }`}
          data-testid="signature-canvas"
        />
      </div>

      <div className="flex justify-between items-center mt-2 gap-3">
        <span className="text-xs text-muted-foreground">
          {disabled
            ? "Assinatura registrada"
            : "Assine aqui com o dedo ou mouse"}
        </span>

        {!disabled && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-primary hover:underline"
            data-testid="signature-clear"
          >
            Limpar
          </button>
        )}
      </div>
    </div>
  );
}